// Owner presets edit: list, photos, save, visibility
import { apiFetchJSON } from '../../core/api.js';
import { buildPresetSpecs, escapeAttr, formatPriceWithRub } from '../../core/format.js';
import { compressImageFile } from '../../core/images.js';
import { getTg } from '../../core/telegram.js';
import { pruneCartAgainstPresets } from '../cart/cart.js';
import { fetchPresets, setLastPresetsList, setPresetsLoadedOnce } from './list.js';
import { normalizeParcelDraft } from './parcel.js';
import { getCurrentUser } from '../../app/app-state.js';
import { getInput, OWNER_LIST_LOADING_HTML } from '../../core/dom.js';
import type { ApiResponse, PresetItem } from '../../types/api.js';
import { bindPresetEditSpecAutocomplete } from '../build/components-autocomplete.js';

const editPresetImages: Record<string, string[]> = {};
let replaceEditIndex: number | null = null;
let replaceEditPresetId: string | null = null;

function renderOwnerPresetEditCard(item: PresetItem): string {
  const pid = String(item.id);
  const imgs = editPresetImages[pid] || item.images || (item.image ? [item.image] : []);
  const inputId = 'preset-edit-image-' + escapeAttr(item.id);
  let imgBlock = '<div class="photo-gallery preset-edit-gallery">';
  imgBlock += '<div class="photo-preview-list preset-edit-preview-list" data-preset-id="' + escapeAttr(item.id) + '">';
  for (let idx = 0; idx < imgs.length; idx++) {
    imgBlock += '<div class="photo-preview-card" data-photo-index="' + idx + '">';
    imgBlock += '<img src="' + escapeAttr(imgs[idx]) + '" alt="" />';
    if (idx === 0) imgBlock += '<span class="photo-preview-badge">Главная</span>';
    imgBlock +=
      '<button type="button" class="photo-preview-edit preset-edit-photo-replace" data-preset-id="' +
      escapeAttr(item.id) +
      '" data-photo-index="' +
      idx +
      '" aria-label="Изменить">';
    imgBlock +=
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
    imgBlock += '</button>';
    imgBlock +=
      '<button type="button" class="photo-preview-delete preset-edit-photo-delete" data-preset-id="' +
      escapeAttr(item.id) +
      '" data-photo-index="' +
      idx +
      '" aria-label="Удалить фото">✕</button></div>';
  }
  imgBlock += '</div>';
  imgBlock += '<label class="photo-add-card preset-edit-photo-card photo-add-inline" for="' + inputId + '">';
  imgBlock +=
    '<input type="file" class="photo-add-input preset-edit-image-input" id="' +
    inputId +
    '" accept="image/*" data-preset-id="' +
    escapeAttr(item.id) +
    '" />';
  imgBlock += '<span class="photo-add-icon"></span>';
  imgBlock += '<span class="photo-add-text">Добавить фото</span>';
  imgBlock += '</label></div>';
  let html = '<article class="preset-edit-card" data-preset-id="' + escapeAttr(item.id) + '">';
  html += '<div class="preset-visibility-row">';
  html +=
    '<span class="preset-visibility-badge' +
    (item.hidden ? ' preset-visibility-badge-hidden' : ' preset-visibility-badge-visible') +
    '">' +
    (item.hidden ? 'Скрыто у пользователей' : 'Показывается пользователям') +
    '</span>';
  html += '</div>';
  html += '<label class="form-label">Название</label>';
  html += '<input class="form-input preset-edit-title" value="' + escapeAttr(item.title) + '" data-preset-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Подзаголовок</label>';
  html +=
    '<input class="form-input preset-edit-subtitle" value="' +
    escapeAttr(item.shortDescription || '') +
    '" placeholder="i5-11400F + RTX 2070S" data-preset-id="' +
    escapeAttr(item.id) +
    '" />';
  html += '<label class="form-label">Цена</label>';
  html +=
    '<div class="input-with-ruble"><input class="form-input preset-edit-price" value="' +
    escapeAttr(item.price) +
    '" data-preset-id="' +
    escapeAttr(item.id) +
    '" /><span class="input-ruble-suffix">₽</span></div>';
  html += '<label class="form-label">Старая цена (необязательно)</label>';
  html +=
    '<div class="input-with-ruble"><input class="form-input preset-edit-old-price" value="' +
    escapeAttr(item.oldPrice || '') +
    '" data-preset-id="' +
    escapeAttr(item.id) +
    '" /><span class="input-ruble-suffix">₽</span></div>';
  html += '<label class="form-label">Ссылка Avito</label>';
  html +=
    '<input class="form-input preset-edit-avito" value="' +
    escapeAttr(item.avitoLink || '') +
    '" data-preset-id="' +
    escapeAttr(item.id) +
    '" placeholder="https://www.avito.ru/..." />';
  html += '<label class="preset-avito-unavail-wrap">';
  html +=
    '<input type="checkbox" class="preset-edit-avito-unavailable" data-preset-id="' +
    escapeAttr(item.id) +
    '"' +
    (item.avitoUnavailable ? ' checked' : '') +
    ' />';
  html += '<span>Временно недоступно на Авито</span></label>';
  const par = normalizeParcelDraft(item.parcel);
  html += '<p class="form-hint form-hint-mt">Габариты упаковки для отправки (необязательно)</p>';
  html += '<label class="form-label">Вес, кг</label>';
  html +=
    '<input class="form-input preset-edit-parcel-weight" type="text" inputmode="decimal" data-preset-id="' +
    escapeAttr(item.id) +
    '" value="' +
    escapeAttr(par.weightKg) +
    '" placeholder="Например 8.5" autocomplete="off" />';
  html += '<div class="parcel-dims-grid">';
  html += '<div><label class="form-label form-label-inline">Длина, см</label>';
  html +=
    '<input class="form-input preset-edit-parcel-len" type="text" inputmode="decimal" data-preset-id="' +
    escapeAttr(item.id) +
    '" value="' +
    escapeAttr(par.lengthCm) +
    '" placeholder="50" autocomplete="off" /></div>';
  html += '<div><label class="form-label form-label-inline">Ширина, см</label>';
  html +=
    '<input class="form-input preset-edit-parcel-wid" type="text" inputmode="decimal" data-preset-id="' +
    escapeAttr(item.id) +
    '" value="' +
    escapeAttr(par.widthCm) +
    '" placeholder="40" autocomplete="off" /></div>';
  html += '<div><label class="form-label form-label-inline">Высота, см</label>';
  html +=
    '<input class="form-input preset-edit-parcel-hgt" type="text" inputmode="decimal" data-preset-id="' +
    escapeAttr(item.id) +
    '" value="' +
    escapeAttr(par.heightCm) +
    '" placeholder="55" autocomplete="off" /></div>';
  html += '</div>';
  const specs = buildPresetSpecs(item);
  html += '<label class="form-label">Процессор</label>';
  html += '<input class="form-input preset-edit-cpu" value="' + escapeAttr(specs.cpu) + '" data-preset-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Видеокарта</label>';
  html += '<input class="form-input preset-edit-gpu" value="' + escapeAttr(specs.gpu) + '" data-preset-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Мат. плата</label>';
  html +=
    '<input class="form-input preset-edit-motherboard" value="' +
    escapeAttr(specs.motherboard) +
    '" data-preset-id="' +
    escapeAttr(item.id) +
    '" />';
  html += '<label class="form-label">ОЗУ</label>';
  html += '<input class="form-input preset-edit-ram" value="' + escapeAttr(specs.ram) + '" data-preset-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Блок питания</label>';
  html += '<input class="form-input preset-edit-psu" value="' + escapeAttr(specs.psu) + '" data-preset-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Накопители</label>';
  html += '<input class="form-input preset-edit-storage" value="' + escapeAttr(specs.storage) + '" data-preset-id="' + escapeAttr(item.id) + '" />';
  html += '<label class="form-label">Корпус</label>';
  html += '<input class="form-input preset-edit-case" value="' + escapeAttr(specs.caseName) + '" data-preset-id="' + escapeAttr(item.id) + '" />';
  html += '<h3 class="photo-section-title preset-edit-photo-title">Фотографии</h3>';
  html += '<div class="preset-edit-image-wrap">' + imgBlock + '</div>';
  html += '<label class="form-label">Описание</label>';
  html +=
    '<textarea class="form-textarea form-textarea-sm preset-edit-desc" data-preset-id="' +
    escapeAttr(item.id) +
    '">' +
    escapeAttr(item.description) +
    '</textarea>';
  html += '<div class="preset-edit-actions">';
  html +=
    '<button type="button" class="submit-btn preset-save-btn" data-preset-id="' +
    escapeAttr(item.id) +
    '">Сохранить изменения</button>';
  html +=
    '<button type="button" class="preset-visibility-toggle-btn" data-preset-id="' +
    escapeAttr(item.id) +
    '" data-hidden="' +
    (item.hidden ? '1' : '0') +
    '">' +
    (item.hidden ? 'Показать пользователям' : 'Скрыть у пользователей') +
    '</button>';
  html += '<button type="button" class="preset-delete-btn" data-preset-id="' + escapeAttr(item.id) + '">Удалить</button>';
  html += '</div></article>';
  return html;
}

export async function loadOwnerEditPresets(): Promise<void> {
  const listEl = document.getElementById('owner-presets-list');
  if (!listEl) return;
  const ownerListEl = listEl;
  listEl.innerHTML = OWNER_LIST_LOADING_HTML;
  const items = await fetchPresets({ includeHidden: true });
  setLastPresetsList(items || []);
  setPresetsLoadedOnce(true);
  try {
    pruneCartAgainstPresets();
  } catch (_) {}
  if (!items.length) {
    listEl.innerHTML = '<p class="consult-text">Нет предложений. Создайте новое во вкладке «Создать новое предложение».</p>';
    return;
  }
  Object.keys(editPresetImages).forEach((k) => delete editPresetImages[k]);
  items.forEach(function (it) {
    editPresetImages[String(it.id)] = it.images || (it.image ? [it.image] : []);
  });
  listEl.innerHTML = items.map(renderOwnerPresetEditCard).join('');
  bindPresetEditSpecAutocomplete(listEl);

  function rebuildPresetEditPhotos(pid: string): void {
    if (!pid) return;
    const list = ownerListEl.querySelector('.preset-edit-preview-list[data-preset-id="' + pid.replace(/"/g, '\\"') + '"]');
    if (!list) return;
    const imgs = editPresetImages[pid] || [];
    list.innerHTML = '';
    for (let idx = 0; idx < imgs.length; idx++) {
      const div = document.createElement('div');
      div.className = 'photo-preview-card';
      div.setAttribute('data-photo-index', String(idx));

      const img = document.createElement('img');
      img.src = imgs[idx];
      img.alt = '';
      div.appendChild(img);

      if (idx === 0) {
        const badge = document.createElement('span');
        badge.className = 'photo-preview-badge';
        badge.textContent = 'Главная';
        div.appendChild(badge);
      }

      const editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'photo-preview-edit preset-edit-photo-replace';
      editBtn.setAttribute('data-preset-id', pid);
      editBtn.setAttribute('data-photo-index', String(idx));
      editBtn.setAttribute('aria-label', 'Изменить');
      editBtn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
      editBtn.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        replaceEditIndex = parseInt(editBtn.getAttribute('data-photo-index') || '0', 10);
        replaceEditPresetId = editBtn.getAttribute('data-preset-id');
        getInput('preset-edit-image-' + pid)?.click();
      });
      div.appendChild(editBtn);

      const delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.className = 'photo-preview-delete preset-edit-photo-delete';
      delBtn.setAttribute('data-preset-id', pid);
      delBtn.setAttribute('data-photo-index', String(idx));
      delBtn.setAttribute('aria-label', 'Удалить фото');
      delBtn.textContent = '✕';
      delBtn.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        const id = delBtn.getAttribute('data-preset-id');
        if (!id) return;
        const delIdx = parseInt(delBtn.getAttribute('data-photo-index') || '0', 10);
        const cur = editPresetImages[id] || [];
        if (typeof delIdx === 'number' && delIdx >= 0 && delIdx < cur.length) {
          cur.splice(delIdx, 1);
          editPresetImages[id] = cur;
        }
        replaceEditIndex = null;
        replaceEditPresetId = null;
        rebuildPresetEditPhotos(id);
      });
      div.appendChild(delBtn);

      list.appendChild(div);
    }
  }

  listEl.querySelectorAll('.preset-edit-image-input').forEach(function (inputEl) {
    const input = inputEl as HTMLInputElement;
    input.addEventListener('change', function () {
      const pid = input.getAttribute('data-preset-id');
      if (!pid || !input.files || !input.files[0]) return;
      const file = input.files[0];
      compressImageFile(file, 1280, 0.8).then(function (dataUrl: string) {
        const imgs = editPresetImages[pid] || [];
        const idx = replaceEditIndex;
        const replacePid = replaceEditPresetId;
        replaceEditIndex = null;
        replaceEditPresetId = null;
        if (typeof idx === 'number' && idx >= 0 && replacePid === pid && imgs[idx] !== undefined) {
          imgs[idx] = dataUrl;
          const card = listEl.querySelector(
            '.preset-edit-preview-list[data-preset-id="' + pid.replace(/"/g, '\\"') + '"] .photo-preview-card[data-photo-index="' + idx + '"]'
          );
          if (card) {
            const imgEl = card.querySelector('img') as HTMLImageElement | null;
            if (imgEl) imgEl.src = dataUrl;
          }
        } else {
          imgs.push(dataUrl);
          const previewList = listEl.querySelector(
            '.preset-edit-preview-list[data-preset-id="' + pid.replace(/"/g, '\\"') + '"]'
          );
          if (previewList) {
            const newIdx = imgs.length - 1;
            const div = document.createElement('div');
            div.className = 'photo-preview-card';
            div.setAttribute('data-photo-index', String(newIdx));
            const img = document.createElement('img');
            img.src = dataUrl;
            img.alt = '';
            div.appendChild(img);
            if (newIdx === 0) {
              const badge = document.createElement('span');
              badge.className = 'photo-preview-badge';
              badge.textContent = 'Главная';
              div.appendChild(badge);
            }
            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'photo-preview-edit preset-edit-photo-replace';
            editBtn.setAttribute('data-preset-id', pid);
            editBtn.setAttribute('data-photo-index', String(newIdx));
            editBtn.setAttribute('aria-label', 'Изменить');
            editBtn.innerHTML =
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
            div.appendChild(editBtn);

            const delBtn = document.createElement('button');
            delBtn.type = 'button';
            delBtn.className = 'photo-preview-delete preset-edit-photo-delete';
            delBtn.setAttribute('data-preset-id', pid);
            delBtn.setAttribute('data-photo-index', String(newIdx));
            delBtn.setAttribute('aria-label', 'Удалить фото');
            delBtn.textContent = '✕';
            div.appendChild(delBtn);

            previewList.appendChild(div);
            editBtn.addEventListener('click', function (ev) {
              ev.preventDefault();
              ev.stopPropagation();
              replaceEditIndex = parseInt(editBtn.getAttribute('data-photo-index') || '0', 10);
              replaceEditPresetId = editBtn.getAttribute('data-preset-id');
              input.click();
            });
            delBtn.addEventListener('click', function (ev) {
              ev.preventDefault();
              ev.stopPropagation();
              const id = delBtn.getAttribute('data-preset-id');
              if (!id) return;
              const delIdx = parseInt(delBtn.getAttribute('data-photo-index') || '0', 10);
              const cur = editPresetImages[id] || [];
              if (typeof delIdx === 'number' && delIdx >= 0 && delIdx < cur.length) {
                cur.splice(delIdx, 1);
                editPresetImages[id] = cur;
              }
              replaceEditIndex = null;
              replaceEditPresetId = null;
              rebuildPresetEditPhotos(id);
            });
          }
        }
        editPresetImages[pid] = imgs;
      });
      input.value = '';
    });
  });

  listEl.querySelectorAll('.preset-edit-photo-replace').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const pid = btn.getAttribute('data-preset-id');
      replaceEditIndex = parseInt(btn.getAttribute('data-photo-index') || '0', 10);
      replaceEditPresetId = pid;
      if (pid) getInput('preset-edit-image-' + pid)?.click();
    });
  });

  listEl.querySelectorAll('.preset-edit-photo-delete').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const id = btn.getAttribute('data-preset-id');
      if (!id) return;
      const delIdx = parseInt(btn.getAttribute('data-photo-index') || '0', 10);
      const cur = editPresetImages[id] || [];
      if (typeof delIdx === 'number' && delIdx >= 0 && delIdx < cur.length) {
        cur.splice(delIdx, 1);
        editPresetImages[id] = cur;
      }
      replaceEditIndex = null;
      replaceEditPresetId = null;
      rebuildPresetEditPhotos(id);
    });
  });

  listEl.querySelectorAll('.preset-save-btn').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', async function (e) {
      e.stopPropagation();
      const t = getTg();
      if (!t || !getCurrentUser()) return;
      const id = btn.getAttribute('data-preset-id');
      if (!id) return;
      const card = listEl.querySelector('.preset-edit-card[data-preset-id="' + id + '"]');
      if (!card) return;
      const titleEl = card.querySelector('.preset-edit-title') as HTMLInputElement | null;
      const subtitleEl = card.querySelector('.preset-edit-subtitle') as HTMLInputElement | null;
      const priceEl = card.querySelector('.preset-edit-price') as HTMLInputElement | null;
      const oldPriceEl = card.querySelector('.preset-edit-old-price') as HTMLInputElement | null;
      const descEl = card.querySelector('.preset-edit-desc') as HTMLTextAreaElement | null;
      const cpuEl = card.querySelector('.preset-edit-cpu') as HTMLInputElement | null;
      const gpuEl = card.querySelector('.preset-edit-gpu') as HTMLInputElement | null;
      const motherboardEl = card.querySelector('.preset-edit-motherboard') as HTMLInputElement | null;
      const ramEl = card.querySelector('.preset-edit-ram') as HTMLInputElement | null;
      const psuEl = card.querySelector('.preset-edit-psu') as HTMLInputElement | null;
      const storageEl = card.querySelector('.preset-edit-storage') as HTMLInputElement | null;
      const caseEl = card.querySelector('.preset-edit-case') as HTMLInputElement | null;
      const parcelWEl = card.querySelector('.preset-edit-parcel-weight') as HTMLInputElement | null;
      const parcelLenEl = card.querySelector('.preset-edit-parcel-len') as HTMLInputElement | null;
      const parcelWidEl = card.querySelector('.preset-edit-parcel-wid') as HTMLInputElement | null;
      const parcelHgtEl = card.querySelector('.preset-edit-parcel-hgt') as HTMLInputElement | null;
      const avitoEl = card.querySelector('.preset-edit-avito') as HTMLInputElement | null;
      const avitoUnavailEl = card.querySelector('.preset-edit-avito-unavailable') as HTMLInputElement | null;
      const title = (titleEl?.value || '').trim();
      const shortDescription = (subtitleEl?.value || '').trim();
      const price = (priceEl?.value || '').trim();
      const oldPrice = (oldPriceEl?.value || '').trim();
      const priceFormatted = formatPriceWithRub(price);
      const oldPriceFormatted = formatPriceWithRub(oldPrice);
      const description = (descEl?.value || '').trim();
      const specs = {
        cpu: (cpuEl?.value || '').trim(),
        gpu: (gpuEl?.value || '').trim(),
        motherboard: (motherboardEl?.value || '').trim(),
        ram: (ramEl?.value || '').trim(),
        psu: (psuEl?.value || '').trim(),
        storage: (storageEl?.value || '').trim(),
        caseName: (caseEl?.value || '').trim()
      };
      const avitoLink = (avitoEl?.value || '').trim();
      const avitoUnavailable = !!(avitoUnavailEl && avitoUnavailEl.checked);
      const parcelPayload = normalizeParcelDraft({
        weightKg: parcelWEl?.value,
        lengthCm: parcelLenEl?.value,
        widthCm: parcelWidEl?.value,
        heightCm: parcelHgtEl?.value
      });
      if (!title) {
        t.showPopup?.({ title: 'Ошибка', message: 'Заполните название.', buttons: [{ type: 'ok' }] });
        return;
      }
      const imgs = editPresetImages[id] || [];
      const payload: {
        id: string;
        title: string;
        shortDescription: string;
        price: string;
        oldPrice: string;
        description: string;
        specs: typeof specs;
        parcel: ReturnType<typeof normalizeParcelDraft>;
        avitoLink: string;
        avitoUnavailable: boolean;
        images: string[];
        image?: string;
      } = {
        id: id,
        title: title,
        shortDescription: shortDescription,
        price: priceFormatted,
        oldPrice: oldPriceFormatted,
        description: description,
        specs: specs,
        parcel: parcelPayload,
        avitoLink: avitoLink,
        avitoUnavailable: avitoUnavailable,
        images: imgs
      };
      if (imgs.length > 0) payload.image = imgs[0];
      btn.disabled = true;
      btn.textContent = 'Сохранение...';
      try {
        const data = await apiFetchJSON<ApiResponse | null>('/api/presets/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(function () {
          return null;
        });
        if (data && data.ok) {
          await loadOwnerEditPresets();
          t.showPopup?.({ title: 'Готово', message: 'Изменения сохранены.', buttons: [{ type: 'ok' }] });
        } else {
          t.showPopup?.({ title: 'Ошибка', message: 'Не удалось сохранить.', buttons: [{ type: 'ok' }] });
        }
      } catch (_) {
        t.showPopup?.({ title: 'Ошибка', message: 'Не удалось сохранить.', buttons: [{ type: 'ok' }] });
      } finally {
        btn.disabled = false;
        btn.textContent = 'Сохранить изменения';
      }
    });
  });

  listEl.querySelectorAll('.preset-edit-card .preset-delete-btn').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', async function (e) {
      e.stopPropagation();
      const t = getTg();
      if (!t || !getCurrentUser()) return;
      if (!confirm('Вы точно хотите удалить объявление?')) return;
      const id = btn.getAttribute('data-preset-id');
      try {
        const data = await apiFetchJSON<ApiResponse | null>('/api/presets/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id })
        }).catch(function () {
          return null;
        });
        if (data && data.ok) {
          await loadOwnerEditPresets();
        } else {
          t.showPopup?.({ title: 'Ошибка', message: 'Не удалось удалить предложение.', buttons: [{ type: 'ok' }] });
        }
      } catch (_) {
        t.showPopup?.({ title: 'Ошибка', message: 'Не удалось удалить предложение.', buttons: [{ type: 'ok' }] });
      }
    });
  });

  listEl.querySelectorAll('.preset-visibility-toggle-btn').forEach(function (btnEl) {
    const btn = btnEl as HTMLButtonElement;
    btn.addEventListener('click', async function (e) {
      e.stopPropagation();
      const t = getTg();
      if (!t || !getCurrentUser()) return;
      const id = btn.getAttribute('data-preset-id');
      const isHiddenNow = btn.getAttribute('data-hidden') === '1';
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Сохранение...';
      try {
        const data = await apiFetchJSON<ApiResponse | null>('/api/presets/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id, hidden: !isHiddenNow })
        }).catch(function () {
          return null;
        });
        if (data && data.ok) {
          await loadOwnerEditPresets();
          t.showPopup?.({
            title: 'Готово',
            message: !isHiddenNow ? 'Сборка скрыта у пользователей.' : 'Сборка снова видна пользователям.',
            buttons: [{ type: 'ok' }]
          });
        } else {
          t.showPopup?.({ title: 'Ошибка', message: 'Не удалось обновить видимость.', buttons: [{ type: 'ok' }] });
        }
      } catch (_) {
        t.showPopup?.({ title: 'Ошибка', message: 'Не удалось обновить видимость.', buttons: [{ type: 'ok' }] });
      } finally {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    });
  });
}
